<template>
    <div class="entity-concepts-flat-table w-100 h-100">
        <MoatTable
            class="w-100 h-100"
            :key="`concepts_${tableUpdater}`"
            :class="themeClass"
            :column-defs="colDefs"
            :tooltip-interaction="true"
            :tooltip-mouse-track="true"
            :tooltip-show-delay="500"
            tooltip-show-mode="whenTruncated"
            :treeData="false"
            :autoSizeStrategy="sizingStrategy"
            :row-data="props.tableData"
            :modules="tableModules"
            :statusBar="statusBar"
            @grid-ready="gridInit"
        />
        <Teleport to="#aon-app .main-content">
            <PatentList
                v-if="showPatentList"
                :passedFilters="initialPatentListFilter"
                @close-patent-list="showPatentList = false"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { ref, onBeforeMount, nextTick, computed } from 'vue'
import { useEntityStore, usePatentViewerStore } from '@/stores'
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { StatusBarModule } from '@ag-grid-enterprise/status-bar'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'

import { useFlag } from '@unleash/proxy-client-vue'
import PatentList from '@/components/patentViewer/PatentList.vue'

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()

const isProUser = useFlag('ipAlpha.proUsers')
const allowPatentList = computed(() => {
    return isProUser.value
})
const patentListViewType = ref('conceptDirect')
const showPatentList = ref(false)
const initialPatentListFilter = ref({
    jurisdiction: 'US',
    legalStatus: ['Active'],
})
const tableModules = [ClientSideRowModelModule, StatusBarModule]
const statusBar = {
    statusPanels: [
        {
            statusPanel: 'agTotalAndFilteredRowCountComponent',
            align: 'left',
        },
    ],
}

const props = defineProps({
    tableData: {
        type: Array,
        default: () => [],
    },
    defaultFilters: {
        type: Object,
        default: () => {},
    },
})

const gridApi = ref(null)
const tableUpdater = ref(0)
const sizingStrategy = ref({
    type: 'fitGridWidth',
})

const colDefs = ref([
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Name',
        },
        field: 'conceptName',
        filter: 'agTextColumnFilter',
        flex: 1,
        minWidth: 200,
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Classification',
        },
        field: 'classification',
        filter: 'agSetColumnFilter',
        flex: 1,
        minWidth: 100,
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Moat Coverage',
        },
        field: 'innovationScore',
        filter: 'agNumberColumnFilter',
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.innovationScore.toLocaleString()
            }
        },
        flex: 1,
        minWidth: 50,
        sort: 'desc',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Patent Count',
        },
        field: 'patentCount',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 50,
        cellRenderer: 'MTCustomCellLinkWContext',
        cellRendererParams: {
            allowClick: allowPatentList.value,
            cellClick: async (params) => {
                patentViewerStore.nodeIdList = [params.data.conceptPk]
                patentViewerStore.technologyName = params.data.conceptName
                if (entityStore.entity) {
                    patentViewerStore.entityPkList.push(entityStore.entity.aon_entity_pk)
                    patentViewerStore.entityName = entityStore.entity.name
                }
                patentViewerStore.listViewType = patentListViewType.value
                await nextTick()
                showPatentList.value = true
            },
        },
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.patentCount.toLocaleString()
            }
        },
    },
    {
        suppressHeaderMenuButton: true,
        cellRenderer: 'MTContextMenu',
        cellRendererParams: {
            actionsList: [
                isProUser.value
                    ? {
                          name: 'View Patents',
                          icon: 'fa-square-list',
                          actionName: 'patents',
                          patentsAction: async (params) => {
                              patentViewerStore.nodeIdList = [params.data.conceptPk]
                              patentViewerStore.technologyName = params.data.conceptName
                              if (entityStore.entity) {
                                  patentViewerStore.entityPkList.push(
                                      entityStore.entity.aon_entity_pk
                                  )
                                  patentViewerStore.entityName = entityStore.entity.name
                              }
                              patentViewerStore.listViewType = patentListViewType.value
                              await nextTick()
                              showPatentList.value = true
                          },
                      }
                    : null,
            ],
            highlightLabel: true,
        },
        pinned: 'right',
        width: 60,
    },
])

const themeClass = ref('ag-theme-quartz')

onBeforeMount(() => {})

const gridInit = (params) => {
    gridApi.value = params.api

    params.api.hideOverlay()
}
</script>
<style lang="scss" scoped>
.entity-concepts-flat-table {
    position: relative;
}
</style>
